<template>
  <div class="fp-page">
    <div class="fp-head">
      <div>
        <h1 class="fp-title"><i class="fa fa-times-circle"></i> Failed payments</h1>
        <p class="fp-sub" v-if="autoRetryEnabled">
          <span class="fp-dot fp-dot-on"></span>
          <strong>Automatic recovery is on.</strong>
          Consultation payments are retried 1, 6 and 24 hours after they fail: first by capturing the card hold from booking,
          then (once the hold has gone) by charging the saved card once. If that fails, the patient is emailed a payment link.
        </p>
        <p class="fp-sub" v-else>
          <span class="fp-dot fp-dot-off"></span>
          <strong>Automatic recovery is switched off</strong> on this server. You can still capture, retry or send a payment link below.
        </p>
      </div>
      <div class="fp-head-actions">
        <b-button size="sm" variant="outline-secondary" @click="showAlerts = true" title="Choose who is emailed when a payment fails">
          <i class="fa fa-bell-o"></i> Alert emails
        </b-button>
        <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="refreshAll">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> Refresh
        </b-button>
      </div>
    </div>

    <b-modal v-model="showAlerts" title="Failed payment alert emails" hide-footer size="md">
      <payment-alert-settings v-if="showAlerts" />
    </b-modal>

    <b-modal v-model="docLink.show" title="Send document payment link" size="md"
             ok-title="Send link" :ok-disabled="docLink.loading || docLink.sending || !docLink.docType"
             @ok.prevent="sendDocLink">
      <div v-if="docLink.loading" class="fp-muted"><b-spinner small /> Checking what the patient needs to pay for…</div>
      <template v-else-if="docLink.row">
        <p>
          Emails <strong>{{ docLink.row.patientName || 'the patient' }}</strong> ({{ docLink.row.email || 'no email' }})
          a secure link to pay for order <strong>#{{ docLink.row.orderItemId }}</strong> with any card, Apple Pay or Google Pay.
        </p>
        <p class="fp-muted" v-if="docLink.row.reasonMessage">Last failure: {{ docLink.row.reasonMessage }}</p>
        <b-form-group label="Paying for" label-size="sm">
          <b-form-select v-model="docLink.docType" :options="docLink.docTypes.map(d => ({ value: d.key, text: d.value }))" size="sm" />
        </b-form-group>
        <p class="fp-muted" v-if="!docLink.docTypes.length">
          The clinician hasn't proposed a document on this order, so there is nothing to send a link for.
          Open the order to check.
        </p>
        <p class="fp-muted mb-0" v-if="docLink.alreadyPaid">
          <i class="fa fa-info-circle"></i> The document payment already shows as paid — only delivery / medication failed.
        </p>
      </template>
    </b-modal>

    <div class="fp-tiles">
      <button type="button" class="fp-tile fp-tile-danger" :class="{ active: tabIndex === 0 && stateFilter === 'NEEDS_ADMIN' }" @click="showState('NEEDS_ADMIN')">
        <span class="fp-tile-label"><i class="fa fa-exclamation-triangle"></i> Needs you</span>
        <span class="fp-tile-value">{{ tiles.needsAdmin }}</span>
        <span class="fp-tile-help">automatic recovery has stopped</span>
      </button>
      <button type="button" class="fp-tile fp-tile-info" :class="{ active: tabIndex === 0 && stateFilter === 'RETRYING' }" @click="showState('RETRYING')">
        <span class="fp-tile-label"><i class="fa fa-clock-o"></i> Retrying</span>
        <span class="fp-tile-value">{{ tiles.retrying }}</span>
        <span class="fp-tile-help">next automatic try is booked</span>
      </button>
      <button type="button" class="fp-tile fp-tile-warn" :class="{ active: tabIndex === 0 && stateFilter === 'LINK_SENT' }" @click="showState('LINK_SENT')">
        <span class="fp-tile-label"><i class="fa fa-envelope-o"></i> Link sent</span>
        <span class="fp-tile-value">{{ tiles.linkSent }}</span>
        <span class="fp-tile-help">waiting for the patient to pay</span>
      </button>
      <div class="fp-tile fp-tile-money">
        <span class="fp-tile-label"><i class="fa fa-gbp"></i> Outstanding</span>
        <span class="fp-tile-value">{{ money(tiles.outstandingPence) }}</span>
        <span class="fp-tile-help">{{ tiles.open }} unpaid consultation{{ tiles.open === 1 ? '' : 's' }}</span>
      </div>
      <button type="button" class="fp-tile fp-tile-ok" :class="{ active: tabIndex === 2 }" @click="tabIndex = 2">
        <span class="fp-tile-label"><i class="fa fa-check-circle"></i> Recovered</span>
        <span class="fp-tile-value">{{ counts.recovered }}</span>
        <span class="fp-tile-help">{{ money(counts.recoveredPence) }} in the last 30 days</span>
      </button>
    </div>

    <b-card no-body class="fp-card">
      <div class="fp-toolbar">
        <b-nav tabs class="fp-tabs">
          <b-nav-item :active="tabIndex === 0" @click="tabIndex = 0">
            Consultation payments <b-badge pill variant="danger" v-if="counts.initial">{{ counts.initial }}</b-badge>
          </b-nav-item>
          <b-nav-item :active="tabIndex === 1" @click="tabIndex = 1">
            Additional payments <b-badge pill variant="warning" v-if="counts.additional">{{ counts.additional }}</b-badge>
          </b-nav-item>
          <b-nav-item :active="tabIndex === 2" @click="tabIndex = 2">Recovered (30 days)</b-nav-item>
        </b-nav>
        <div class="fp-toolbar-right">
          <b-badge v-if="stateFilter && tabIndex === 0" class="fp-filter-chip" variant="light" @click="stateFilter = ''">
            {{ stateLabel(stateFilter) }} <i class="fa fa-times"></i>
          </b-badge>
          <b-form-select v-if="reasonOptions.length > 1" v-model="reasonFilter" size="sm" class="fp-reason-filter" :options="reasonOptions" />
          <b-form-checkbox v-if="tabIndex === 0" v-model="noCardOnly" size="sm" class="fp-nocard">No saved card</b-form-checkbox>
          <div class="fp-search">
            <i class="fa fa-search"></i>
            <b-form-input v-model="search" size="sm" placeholder="Name, email, phone or order #" />
          </div>
        </div>
      </div>

      <div v-if="loading && !rows.length" class="fp-empty"><b-spinner small /> Loading…</div>
      <div v-else-if="!filteredRows.length" class="fp-empty">
        <i class="fa fa-check-circle-o"></i>
        <strong>{{ tabIndex === 2 ? 'Nothing recovered in the last 30 days' : 'No failed payments here' }}</strong>
        <span v-if="search || stateFilter || reasonFilter || noCardOnly">Try clearing the search or filters.</span>
      </div>

      <div v-else class="fp-list">
        <div
          v-for="row in filteredRows"
          :key="row.kind + row.orderItemId"
          class="fp-row"
          :class="['fp-row-' + stateTone(row.state), { open: expanded[row.orderItemId] }]"
        >
          <div class="fp-row-main" @click="toggle(row)">
            <div class="fp-col-order">
              <router-link :to="orderLink(row)" class="fp-order" @click.native.stop>#{{ row.orderItemId }}</router-link>
              <div class="fp-muted">{{ when(row.failedAt) }}</div>
              <div class="fp-muted" v-if="row.failedAt">{{ ago(row.failedAt) }}</div>
            </div>

            <div class="fp-col-patient">
              <router-link v-if="row.patientId" :to="'/patient/' + row.patientId" class="fp-patient" @click.native.stop
                           title="Open the patient's record">{{ row.patientName || 'Unknown patient' }}</router-link>
              <div v-else class="fp-patient">{{ row.patientName || 'Unknown patient' }}</div>
              <div class="fp-muted" v-if="row.email">{{ row.email }}</div>
              <div class="fp-muted" v-if="row.phone">{{ row.phone }}</div>
            </div>

            <div class="fp-col-amount">
              <div class="fp-amount">{{ money(row.amountPence) }}</div>
              <div class="fp-muted" v-if="cardLabel(row.reasonDetail)"><i class="fa fa-credit-card"></i> {{ cardLabel(row.reasonDetail) }}</div>
            </div>

            <div class="fp-col-reason">
              <div class="fp-reason">{{ reasonHeadline(row) }}</div>
              <div class="fp-advice" v-if="row.kind === 'INITIAL' && row.state !== 'RECOVERED' && advice(row)">
                <i class="fa fa-lightbulb-o"></i> {{ advice(row) }}
              </div>
              <div class="fp-chips">
                <span class="fp-chip fp-chip-code" v-if="row.reasonCode" :title="'Stripe code'">{{ row.reasonCode }}</span>
                <span class="fp-chip" v-if="detail(row).networkStatus" :title="'Card network response'">bank: {{ humanise(detail(row).networkStatus) }}</span>
                <span class="fp-chip" :class="'fp-risk-' + detail(row).riskLevel" v-if="detail(row).riskLevel && detail(row).riskLevel !== 'normal'">risk: {{ detail(row).riskLevel }}</span>
                <span class="fp-chip fp-chip-warn" v-for="part in (row.failedParts || [])" :key="part">{{ part }}</span>
              </div>
            </div>

            <div class="fp-col-state" v-if="row.kind === 'INITIAL'">
              <span class="fp-state" :class="'fp-state-' + stateTone(row.state)">{{ stateLabel(row.state) }}</span>
              <div class="fp-muted" v-if="row.state === 'RETRYING' && row.nextAttemptAt">Next try {{ when(row.nextAttemptAt) }}</div>
              <div class="fp-muted" v-if="row.state === 'RETRYING'">{{ row.autoAttempts }}/3 automatic tries done</div>
              <div class="fp-muted" v-if="row.linkSentAt">Link emailed {{ when(row.linkSentAt) }}</div>
              <div class="fp-muted" v-if="row.recoveredAt">Paid {{ when(row.recoveredAt) }} · {{ recoveredByLabel(row.recoveredBy) }}</div>
              <div class="fp-muted fp-warn-text" v-if="!row.hasSavedCard && row.state !== 'RECOVERED'"><i class="fa fa-ban"></i> No saved card</div>
            </div>

            <div class="fp-col-toggle">
              <i class="fa" :class="expanded[row.orderItemId] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
          </div>

          <div class="fp-actions" v-if="row.kind === 'INITIAL' && row.state !== 'RECOVERED'">
            <b-button
              v-if="primaryAction(row) === 'capture'"
              size="sm"
              variant="success"
              :disabled="isBusy(row)"
              title="Take the money from the card hold made when the patient booked"
              @click="confirmCapture(row)"
            >
              <b-spinner small v-if="busy[row.orderItemId] === 'capture'" /><i v-else class="fa fa-lock"></i> Capture again
            </b-button>
            <b-button
              v-else-if="primaryAction(row) === 'charge'"
              size="sm"
              variant="success"
              :disabled="isBusy(row)"
              title="The card hold has gone — charge the patient's saved card once"
              @click="confirmCharge(row)"
            >
              <b-spinner small v-if="busy[row.orderItemId] === 'retry'" /><i v-else class="fa fa-credit-card"></i> Charge saved card
            </b-button>
            <b-button size="sm" :variant="primaryAction(row) ? 'outline-primary' : 'primary'" :disabled="isBusy(row)"
                      title="Email the patient a secure Stripe link to pay with any card" @click="confirmLink(row)">
              <b-spinner small v-if="busy[row.orderItemId] === 'link'" /><i v-else class="fa fa-envelope-o"></i>
              {{ row.paymentLinkUrl ? 'Resend payment link' : 'Send payment link' }}
            </b-button>
            <span class="fp-actions-spacer"></span>
            <b-dropdown size="sm" variant="link" right no-caret toggle-class="fp-more" :disabled="isBusy(row)">
              <template #button-content><i class="fa fa-ellipsis-h"></i> More</template>
              <b-dropdown-item v-if="row.paymentLinkUrl" @click="copyLink(row)"><i class="fa fa-clone"></i> Copy payment link</b-dropdown-item>
              <b-dropdown-item :to="orderLink(row)"><i class="fa fa-gbp"></i> Record manual payment</b-dropdown-item>
              <b-dropdown-item v-if="row.patientId" :to="'/patient/' + row.patientId"><i class="fa fa-user"></i> Open patient</b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="fp-actions" v-else-if="row.kind === 'ADDITIONAL'">
            <b-button size="sm" variant="primary" :disabled="isBusy(row)" @click="openDocLink(row)"
                      title="Email the patient a link to pay for the document with a new card">
              <b-spinner small v-if="busy[row.orderItemId] === 'doclink'" /><i v-else class="fa fa-envelope-o"></i> Send payment link
            </b-button>
            <span class="fp-muted fp-actions-note">
              <i class="fa fa-info-circle"></i> Additional payments can't be captured again from here — the patient pays with the link.
            </span>
            <span class="fp-actions-spacer"></span>
            <b-button size="sm" variant="outline-secondary" :to="orderLink(row)">Open order</b-button>
          </div>

          <div class="fp-details" v-if="expanded[row.orderItemId]">
            <div class="fp-panel">
              <h3><i class="fa fa-search"></i> Why it failed — full detail</h3>
              <p class="fp-panel-lead">{{ reasonHeadline(row) }}</p>
              <p class="fp-muted" v-if="row.reasonMessage && row.reasonMessage !== reasonHeadline(row)">“{{ row.reasonMessage }}”</p>
              <dl class="fp-dl" v-if="detailRows(detail(row)).length">
                <template v-for="d in detailRows(detail(row))">
                  <dt :key="'t' + d.key">{{ d.label }}</dt>
                  <dd :key="'d' + d.key">
                    <a v-if="d.link" :href="d.value" target="_blank" rel="noopener">Stripe docs <i class="fa fa-external-link"></i></a>
                    <code v-else-if="d.code">{{ d.value }}</code>
                    <span v-else>{{ d.value }}</span>
                  </dd>
                </template>
              </dl>
              <p v-else class="fp-muted">Stripe didn't return more detail for this failure (older failures only stored the message).</p>
            </div>

            <div class="fp-panel" v-if="row.kind === 'INITIAL'">
              <h3><i class="fa fa-lock"></i> Card hold from booking <small>live from Stripe</small></h3>
              <div v-if="!holds[row.orderItemId]" class="fp-muted"><b-spinner small /> Checking Stripe…</div>
              <template v-else>
                <div v-if="!holds[row.orderItemId].available" class="fp-hold fp-hold-unknown">
                  <i class="fa fa-question-circle"></i> {{ holds[row.orderItemId].reason }}
                </div>
                <template v-else>
                  <div class="fp-hold" :class="holds[row.orderItemId].capturable ? 'fp-hold-ok' : 'fp-hold-gone'">
                    <i class="fa" :class="holds[row.orderItemId].capturable ? 'fa-check-circle' : 'fa-times-circle'"></i>
                    <span v-if="holds[row.orderItemId].capturable">
                      Can be captured: <strong>{{ money(holds[row.orderItemId].amountCapturablePence) }}</strong>
                      <span v-if="holds[row.orderItemId].holdExpiresAt"> · usually lapses {{ when(holds[row.orderItemId].holdExpiresAt) }}</span>
                    </span>
                    <span v-else>Can't be captured — {{ holdStatusLabel(holds[row.orderItemId]) }}</span>
                  </div>
                  <dl class="fp-dl">
                    <template v-for="d in detailRows(holds[row.orderItemId], HOLD_KEYS)">
                      <dt :key="'ht' + d.key">{{ d.label }}</dt>
                      <dd :key="'hd' + d.key"><code v-if="d.code">{{ d.value }}</code><span v-else>{{ d.value }}</span></dd>
                    </template>
                  </dl>
                </template>
              </template>
            </div>

            <div class="fp-panel fp-panel-wide" v-if="row.kind === 'INITIAL'">
              <h3><i class="fa fa-history"></i> Attempts</h3>
              <div v-if="!history[row.orderItemId]" class="fp-muted"><b-spinner small /> Loading…</div>
              <div v-else-if="!history[row.orderItemId].length" class="fp-muted">No attempts recorded yet.</div>
              <ol v-else class="fp-timeline">
                <li v-for="a in history[row.orderItemId]" :key="a.id" :class="a.succeeded ? 'ok' : 'bad'">
                  <div class="fp-tl-head">
                    <strong>{{ methodLabel(a.method) }}</strong>
                    <span class="fp-state" :class="a.succeeded ? 'fp-state-ok' : 'fp-state-bad'">{{ a.succeeded ? 'Worked' : 'Failed' }}</span>
                    <span class="fp-muted">{{ when(a.createdAt) }} · {{ triggerLabel(a) }}<span v-if="a.amountPence"> · {{ money(a.amountPence) }}</span></span>
                  </div>
                  <div v-if="!a.succeeded" class="fp-tl-reason">
                    <span class="fp-chip fp-chip-code" v-if="a.failureCode">{{ a.failureCode }}</span>
                    {{ labelFor(a.failureCode) || a.failureMessage || 'No reason given' }}
                    <span class="fp-muted" v-if="a.failureDetail && a.failureDetail.sellerMessage"> — {{ a.failureDetail.sellerMessage }}</span>
                  </div>
                  <div v-if="a.stripeId" class="fp-muted">Stripe: <code>{{ a.stripeId }}</code></div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';
import PaymentAlertSettings from './PaymentAlertSettings';
import { docTypesFromLog, isDocumentPaid, sendDocumentPaymentLink } from '@/services/documentPaymentLink';

const VIEWS = ['initial', 'additional', 'recovered'];

/** Plain-English versions of the Stripe decline codes admins see most. */
const DECLINE_LABELS = {
  insufficient_funds: 'Not enough money in the account',
  card_declined: 'Card declined by the bank',
  generic_decline: 'Card declined by the bank (no reason given)',
  do_not_honor: 'Bank refused the payment',
  expired_card: 'Card has expired',
  incorrect_cvc: 'Security code was wrong',
  incorrect_number: 'Card number was wrong',
  lost_card: 'Card reported lost',
  stolen_card: 'Card reported stolen',
  fraudulent: 'Blocked as suspected fraud',
  card_velocity_exceeded: 'Card spending limit reached',
  withdrawal_count_limit_exceeded: 'Card spending limit reached',
  authentication_required: 'Bank needs the patient to approve it (3D Secure)',
  processing_error: 'Stripe processing error — worth retrying',
  no_saved_card: 'No saved card to charge',
  no_hold: 'No card hold was taken at booking',
  hold_canceled: 'Card hold from booking was cancelled or expired',
  hold_requires_payment_method: 'Card hold from booking failed',
  hold_expired: 'Card hold from booking expired'
};

/** What the admin should do next, by reason. */
const ADVICE = {
  insufficient_funds: 'Retrying later can work (payday); otherwise send a payment link so they can use another card.',
  card_velocity_exceeded: 'Retry tomorrow, or send a link so they can use another card.',
  withdrawal_count_limit_exceeded: 'Retry tomorrow, or send a link so they can use another card.',
  expired_card: 'This card can\'t be used again — send a payment link for a new card.',
  lost_card: 'Don\'t retry this card — send a payment link for a new card.',
  stolen_card: 'Don\'t retry this card — send a payment link for a new card.',
  incorrect_cvc: 'Send a payment link so they can re-enter the card.',
  incorrect_number: 'Send a payment link so they can re-enter the card.',
  authentication_required: 'Send a payment link — the patient approves it with their bank (3D Secure).',
  fraudulent: 'Check with the patient before taking payment again.',
  processing_error: 'Temporary problem — use Retry payment.',
  do_not_honor: 'The bank gave no detail — retry once, then send a link and ask them to call their bank.',
  generic_decline: 'The bank gave no detail — retry once, then send a link and ask them to call their bank.',
  card_declined: 'Retry once, then send a link and ask them to call their bank.',
  hold_canceled: 'The hold has gone — Retry payment charges the saved card, or send a link.',
  hold_expired: 'The hold has gone — Retry payment charges the saved card, or send a link.',
  no_saved_card: 'Send a payment link.',
  no_hold: 'Retry payment charges the saved card, or send a link.'
};

const DETAIL_LABELS = {
  declineCode: ['Bank decline code', true],
  code: ['Stripe error code', true],
  message: ['Stripe message', false],
  errorType: ['Error type', false],
  outcomeType: ['Outcome', false],
  outcomeReason: ['Outcome reason', false],
  networkStatus: ['Card network response', false],
  sellerMessage: ['What Stripe says', false],
  chargeFailureMessage: ['Charge failure message', false],
  riskLevel: ['Stripe risk level', false],
  riskScore: ['Stripe risk score', false],
  cardBrand: ['Card', false],
  cardLast4: ['Card number', false],
  cardExpiry: ['Card expiry', false],
  cardFunding: ['Card type', false],
  cardCountry: ['Card country', false],
  threeDSecure: ['3D Secure', false],
  wallet: ['Wallet', false],
  paymentIntentStatus: ['Payment status', true],
  paymentIntentId: ['Payment intent', true],
  chargeId: ['Charge', true],
  requestId: ['Stripe request', true],
  httpStatus: ['HTTP status', false],
  docUrl: ['More info', false]
};

const HOLD_KEYS = ['paymentIntentStatus', 'amountPence', 'amountCapturablePence', 'createdAt', 'holdExpiresAt',
  'canceledAt', 'cancellationReason', 'cardBrand', 'cardLast4', 'cardExpiry', 'declineCode', 'message',
  'paymentIntentId', 'livemode'];

const HOLD_LABELS = {
  amountPence: 'Amount held',
  amountCapturablePence: 'Still capturable',
  createdAt: 'Hold taken',
  holdExpiresAt: 'Usually lapses',
  canceledAt: 'Cancelled',
  cancellationReason: 'Why cancelled',
  livemode: 'Mode'
};

export default {
  components: { PaymentAlertSettings },
  data () {
    return {
      HOLD_KEYS,
      showAlerts: false,
      reasonFilter: '',
      noCardOnly: false,
      docLink: { show: false, loading: false, sending: false, row: null, docTypes: [], docType: '', prescriptionType: null, alreadyPaid: false },
      tabIndex: 0,
      rows: [],
      initialRows: [],
      counts: { initial: 0, additional: 0, recovered: 0, recoveredPence: 0 },
      autoRetryEnabled: true,
      loading: false,
      search: '',
      stateFilter: '',
      busy: {},
      expanded: {},
      history: {},
      holds: {}
    };
  },
  computed: {
    view () {
      return VIEWS[this.tabIndex] || 'initial';
    },
    tiles () {
      const rows = this.initialRows;
      const count = (state) => rows.filter((r) => r.state === state).length;
      return {
        needsAdmin: rows.filter((r) => r.state === 'NEEDS_ADMIN' || !r.state).length,
        retrying: count('RETRYING'),
        linkSent: count('LINK_SENT'),
        open: rows.length,
        outstandingPence: rows.reduce((sum, r) => sum + (r.amountPence || 0), 0)
      };
    },
    /** "Why it failed" filter, built from the reasons on this tab, most common first. */
    reasonOptions () {
      const counts = {};
      this.rows.forEach((r) => {
        const key = this.reasonKey(r);
        counts[key] = (counts[key] || 0) + 1;
      });
      const options = Object.keys(counts)
        .sort((a, b) => counts[b] - counts[a])
        .map((key) => ({ value: key, text: this.reasonKeyLabel(key) + ' (' + counts[key] + ')' }));
      return [{ value: '', text: 'All reasons (' + this.rows.length + ')' }].concat(options);
    },
    filteredRows () {
      const q = (this.search || '').trim().toLowerCase();
      return this.rows.filter((r) => {
        if (this.stateFilter && this.view === 'initial') {
          const state = r.state || 'NEEDS_ADMIN';
          if (state !== this.stateFilter) return false;
        }
        if (this.reasonFilter && this.reasonKey(r) !== this.reasonFilter) return false;
        if (this.noCardOnly && this.view === 'initial' && r.hasSavedCard) return false;
        if (!q) return true;
        return [r.patientName, r.email, r.phone, String(r.orderItemId)]
          .some((v) => v && String(v).toLowerCase().indexOf(q) >= 0);
      });
    }
  },
  watch: {
    tabIndex () {
      if (this.tabIndex !== 0) {
        this.stateFilter = '';
        this.noCardOnly = false;
      }
      this.reasonFilter = '';
      this.load();
    }
  },
  created () {
    this.refreshAll();
  },
  methods: {
    refreshAll () {
      this.load();
      this.loadCounts();
    },
    load () {
      this.loading = true;
      const view = this.view;
      this.$orderService.getFailedPayments(view).then((res) => {
        if (view !== this.view) return;
        const data = (res && res.data) || {};
        this.rows = data.rows || [];
        this.expanded = {};
        this.autoRetryEnabled = data.autoRetryEnabled !== false;
        if (view === 'initial') {
          this.initialRows = this.rows;
          this.counts.initial = this.rows.length;
        }
        if (view === 'additional') this.counts.additional = this.rows.length;
        if (view === 'recovered') this.setRecovered(this.rows);
      }).catch(() => {
        this.$awn.alert('Could not load failed payments');
      }).finally(() => {
        this.loading = false;
      });
    },
    loadCounts () {
      const other = (view, apply) => this.$orderService.getFailedPayments(view)
        .then((res) => apply(((res && res.data) || {}).rows || [])).catch(() => {});
      if (this.view !== 'initial') {
        other('initial', (rows) => { this.initialRows = rows; this.counts.initial = rows.length; });
      }
      if (this.view !== 'additional') other('additional', (rows) => { this.counts.additional = rows.length; });
      if (this.view !== 'recovered') other('recovered', this.setRecovered);
    },
    setRecovered (rows) {
      this.counts.recovered = rows.length;
      this.counts.recoveredPence = rows.reduce((sum, r) => sum + (r.amountPence || 0), 0);
    },
    showState (state) {
      this.stateFilter = this.stateFilter === state && this.tabIndex === 0 ? '' : state;
      this.tabIndex = 0;
    },
    toggle (row) {
      const id = row.orderItemId;
      const open = !this.expanded[id];
      this.$set(this.expanded, id, open);
      if (!open || row.kind !== 'INITIAL') return;
      if (!this.history[id]) this.loadHistory(id);
      if (!this.holds[id] && row.state !== 'RECOVERED') this.loadHold(id);
      if (row.state === 'RECOVERED') this.$set(this.holds, id, { available: false, reason: 'Payment already recovered' });
    },
    loadHistory (id) {
      this.$orderService.getFailedPaymentAttempts(id).then((res) => {
        this.$set(this.history, id, (res && res.data) || []);
      }).catch(() => {
        this.$set(this.history, id, []);
      });
    },
    loadHold (id) {
      this.$orderService.getFailedPaymentHold(id).then((res) => {
        this.$set(this.holds, id, (res && res.data) || { available: false, reason: 'No answer from Stripe' });
      }).catch((error) => {
        this.$set(this.holds, id, { available: false, reason: this.errorMessage(error, 'Could not check the hold') });
      });
    },
    detail (row) {
      return (row && row.reasonDetail) || {};
    },
    detailRows (d, keys) {
      if (!d) return [];
      const order = keys || Object.keys(DETAIL_LABELS);
      return order.filter((k) => d[k] !== undefined && d[k] !== null && d[k] !== '').map((k) => {
        const known = DETAIL_LABELS[k];
        let value = d[k];
        if (k === 'amountPence' || k === 'amountCapturablePence') value = this.money(value);
        else if (/At$/.test(k)) value = this.when(value);
        else if (k === 'cardLast4') value = '•••• ' + value;
        else if (k === 'livemode') value = value ? 'Live' : 'Test';
        else if (k === 'declineCode' || k === 'code') value = value + (DECLINE_LABELS[value] ? ' — ' + DECLINE_LABELS[value] : '');
        else if (typeof value === 'string' && /^[a-z_]+$/.test(value) && !(known && known[1])) value = this.humanise(value);
        return {
          key: k,
          label: HOLD_LABELS[k] || (known ? known[0] : this.humanise(k)),
          value,
          code: !!(known && known[1]) && k !== 'declineCode' && k !== 'code' ? true : false,
          link: k === 'docUrl'
        };
      });
    },
    cardLabel (d) {
      if (!d || !d.cardLast4) return '';
      return (d.cardBrand ? this.humanise(d.cardBrand) + ' ' : '') + '•••• ' + d.cardLast4;
    },
    humanise (value) {
      if (value === null || value === undefined) return '';
      const s = String(value).replace(/_/g, ' ');
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    /** True when we already know there is no hold to capture (live check, or what Stripe said at failure). */
    holdKnownDead (row) {
      const h = this.holds[row.orderItemId];
      if (h) return !!(h.available && !h.capturable);
      const status = this.detail(row).paymentIntentStatus;
      return status === 'requires_payment_method' || status === 'canceled'
        || row.reasonCode === 'no_hold' || /^hold_/.test(row.reasonCode || '');
    },
    /** The one action that fits this failure: capture the hold while it lasts, else the saved card, else only the link. */
    primaryAction (row) {
      if (!this.holdKnownDead(row)) return 'capture';
      return row.hasSavedCard ? 'charge' : null;
    },
    /** Exact reason used by the filter: the bank's decline code if we have it, else our code. */
    reasonKey (row) {
      const d = this.detail(row);
      return d.declineCode || row.reasonCode || (row.reasonMessage ? 'msg:' + row.reasonMessage : 'unknown');
    },
    reasonKeyLabel (key) {
      if (key === 'unknown') return 'Reason not recorded';
      if (key.indexOf('msg:') === 0) {
        const msg = key.slice(4);
        return msg.length > 60 ? msg.slice(0, 60) + '…' : msg;
      }
      return DECLINE_LABELS[key] ? DECLINE_LABELS[key] + ' · ' + key : this.humanise(key);
    },
    holdStatusLabel (h) {
      const s = h.paymentIntentStatus;
      if (s === 'canceled') return 'Stripe cancelled it' + (h.cancellationReason ? ' (' + this.humanise(h.cancellationReason).toLowerCase() + ')' : '');
      if (s === 'succeeded') return 'it was already captured';
      if (s === 'requires_payment_method') return 'the card was declined when it was taken';
      return this.humanise(s || 'unknown').toLowerCase();
    },
    labelFor (code) {
      return DECLINE_LABELS[code] || '';
    },
    orderLink (row) {
      return row.kind === 'ADDITIONAL' ? '/order/' + row.orderItemId : '/order/payment-failed/' + row.orderItemId;
    },
    money (pence) {
      if (pence === null || pence === undefined) return '-';
      return (pence / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
    },
    when (value) {
      return value ? moment(value).local().format('DD/MM/YY HH:mm') : '-';
    },
    ago (value) {
      return value ? moment(value).fromNow() : '';
    },
    reasonHeadline (row) {
      const d = this.detail(row);
      return DECLINE_LABELS[d.declineCode] || DECLINE_LABELS[row.reasonCode] || row.reasonMessage || 'Reason not recorded';
    },
    advice (row) {
      const d = this.detail(row);
      if (d.riskLevel === 'highest' || d.outcomeType === 'blocked') return ADVICE.fraudulent;
      return ADVICE[d.declineCode] || ADVICE[row.reasonCode] || (row.hasSavedCard ? '' : ADVICE.no_saved_card);
    },
    stateLabel (state) {
      return {
        RETRYING: 'Retrying automatically',
        LINK_SENT: 'Payment link sent',
        NEEDS_ADMIN: 'Needs admin',
        RECOVERED: 'Recovered',
        CLOSED: 'Closed'
      }[state] || 'Not tracked';
    },
    stateTone (state) {
      return {
        RETRYING: 'info',
        LINK_SENT: 'warn',
        NEEDS_ADMIN: 'bad',
        RECOVERED: 'ok',
        CLOSED: 'muted'
      }[state] || 'bad';
    },
    recoveredByLabel (by) {
      if (!by) return '';
      if (by === 'PATIENT') return 'patient paid the link';
      if (by === 'AUTO') return 'automatic retry';
      if (by.indexOf('ADMIN:') === 0) return 'admin';
      return by.toLowerCase();
    },
    methodLabel (method) {
      return {
        ORIGINAL: 'Original payment',
        HOLD_CAPTURE: 'Capture card hold',
        SAVED_CARD: 'Charge saved card',
        PAYMENT_LINK: 'Payment link'
      }[method] || method;
    },
    triggerLabel (a) {
      if (a.attemptedBy === 'PATIENT') return 'Patient';
      if (a.trigger === 'ADMIN') return 'Admin';
      if (a.trigger === 'DOCTOR_COMPLETE') return 'Doctor completed consult';
      if (a.trigger === 'DISCOVERED') return 'Picked up automatically';
      return 'Automatic';
    },
    isBusy (row) {
      return !!this.busy[row.orderItemId];
    },
    errorMessage (error, fallback) {
      const data = error && error.response && error.response.data;
      return (data && (data.message || data.error)) || (error && error.message) || fallback;
    },
    confirmCapture (row) {
      const text = 'Capture ' + this.money(row.amountPence) + ' from the card hold ' + (row.patientName || 'this patient')
        + ' made when booking? This only uses the hold — it never charges their saved card.';
      this.$bvModal.msgBoxConfirm(text, { title: 'Capture card hold', okTitle: 'Capture', okVariant: 'success', cancelTitle: 'Cancel' })
        .then((ok) => { if (ok) this.runAction(row, 'capture'); });
    },
    confirmCharge (row) {
      const card = this.cardLabel(this.detail(row));
      const text = 'Charge ' + this.money(row.amountPence) + ' to ' + (row.patientName || 'this patient') + '\'s saved card'
        + (card ? ' (' + card + ')' : '') + ' now? If the booking hold is somehow still there it is captured instead, '
        + 'so the patient is never charged twice.';
      this.$bvModal.msgBoxConfirm(text, { title: 'Charge saved card', okTitle: 'Charge card', okVariant: 'success', cancelTitle: 'Cancel' })
        .then((ok) => { if (ok) this.runAction(row, 'retry'); });
    },
    confirmLink (row) {
      if (!row.email) {
        this.$awn.alert('This patient has no email address — add one on their record first');
        return;
      }
      const text = 'Email ' + (row.patientName || 'the patient') + ' (' + row.email + ') a secure link to pay '
        + this.money(row.amountPence) + '?' + (row.paymentLinkUrl ? ' The old link stops being the latest one.' : '')
        + ' Automatic retries stop once the link is sent.';
      this.$bvModal.msgBoxConfirm(text, { title: row.paymentLinkUrl ? 'Resend payment link' : 'Send payment link', okTitle: 'Send', cancelTitle: 'Cancel' })
        .then((ok) => { if (ok) this.sendLink(row); });
    },
    openDocLink (row) {
      this.docLink = { show: true, loading: true, sending: false, row, docTypes: [], docType: '', prescriptionType: null, alreadyPaid: false };
      const id = row.orderItemId;
      Promise.all([
        this.$orderService.getConsentLog(id).catch(() => null),
        this.$orderService.getAdditionalPaymentStatus(id).catch(() => null),
        this.$orderService.getPrescription(id).catch(() => null)
      ]).then(([log, status, prescription]) => {
        if (this.docLink.row !== row) return;
        const rows = (log && log.data) || [];
        const latest = Object.assign({}, rows[rows.length - 1] || {}, (status && status.data) || {});
        const types = docTypesFromLog(latest);
        this.docLink.docTypes = types;
        this.docLink.docType = types.length ? types[0].key : '';
        this.docLink.prescriptionType = prescription && prescription.prescriptionType;
        this.docLink.alreadyPaid = isDocumentPaid(latest.documentPaymentStatus);
      }).finally(() => {
        this.docLink.loading = false;
      });
    },
    sendDocLink () {
      const { row, docType, prescriptionType } = this.docLink;
      if (!row || !docType) return;
      this.docLink.sending = true;
      this.$set(this.busy, row.orderItemId, 'doclink');
      sendDocumentPaymentLink(this.$orderService, {
        orderId: row.orderItemId,
        patientId: row.patientId,
        email: row.email,
        docType,
        prescriptionType
      }).then(() => {
        this.$awn.success('Payment link emailed to ' + row.email);
        this.docLink.show = false;
      }).catch((error) => {
        this.$awn.alert(this.errorMessage(error, 'Could not send the payment link'));
      }).finally(() => {
        this.docLink.sending = false;
        this.$delete(this.busy, row.orderItemId);
      });
    },
    runAction (row, kind) {
      this.$set(this.busy, row.orderItemId, kind);
      const call = kind === 'capture'
        ? this.$orderService.captureFailedPaymentHold(row.orderItemId)
        : this.$orderService.retryFailedPayment(row.orderItemId);
      call.then((res) => {
        const result = (res && res.data) || {};
        if (result.state === 'RECOVERED') {
          this.$awn.success('Payment taken for order #' + row.orderItemId);
        } else {
          const reason = DECLINE_LABELS[result.reasonCode] || result.reasonMessage || 'no reason given';
          this.$awn.warning('Payment still failed: ' + reason + (result.reasonCode ? ' (' + result.reasonCode + ')' : ''));
        }
        this.refreshRow(row);
      }).catch((error) => {
        this.$awn.alert(this.errorMessage(error, kind === 'capture' ? 'Capture failed' : 'Retry failed'));
      }).finally(() => {
        this.$delete(this.busy, row.orderItemId);
      });
    },
    sendLink (row) {
      this.$set(this.busy, row.orderItemId, 'link');
      this.$orderService.sendFailedPaymentLink(row.orderItemId).then(() => {
        this.$awn.success('Payment link emailed to ' + (row.email || 'the patient'));
        this.refreshRow(row);
      }).catch((error) => {
        this.$awn.alert(this.errorMessage(error, 'Could not send the payment link'));
      }).finally(() => {
        this.$delete(this.busy, row.orderItemId);
      });
    },
    copyLink (row) {
      const done = () => this.$awn.success('Link copied');
      if (navigator.clipboard) {
        navigator.clipboard.writeText(row.paymentLinkUrl).then(done).catch(() => window.prompt('Copy the link', row.paymentLinkUrl));
      } else {
        window.prompt('Copy the link', row.paymentLinkUrl);
      }
    },
    refreshRow (row) {
      this.$delete(this.history, row.orderItemId);
      this.$delete(this.holds, row.orderItemId);
      this.refreshAll();
    }
  }
};
</script>

<style scoped>
.fp-head-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
.fp-reason-filter {
  width: auto;
  max-width: 280px;
}
.fp-nocard {
  white-space: nowrap;
  font-size: 0.85rem;
}
.fp-actions-note {
  font-size: 0.8rem;
}
a.fp-patient {
  color: inherit;
  text-decoration: underline;
  text-decoration-color: #b8c7c1;
}
a.fp-patient:hover {
  color: #0f6a7c;
}
.fp-page {
  --fp-ink: #17333a;
  --fp-muted: #64748b;
  --fp-line: #dfe7e3;
  --fp-bad: #b42318;
  --fp-bad-bg: #fdecea;
  --fp-warn: #a15c07;
  --fp-warn-bg: #fdf3e2;
  --fp-info: #0f6a7c;
  --fp-info-bg: #e7f4f7;
  --fp-ok: #176b3a;
  --fp-ok-bg: #e7f7ee;
  color: var(--fp-ink);
}
.fp-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}
.fp-title {
  font-size: 1.45rem;
  font-weight: 800;
  margin: 0 0 0.3rem;
}
.fp-title .fa { color: var(--fp-bad); }
.fp-sub {
  margin: 0;
  max-width: 820px;
  font-size: 0.86rem;
  color: var(--fp-muted);
}
.fp-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.3rem;
  vertical-align: middle;
}
.fp-dot-on { background: #1f8a5b; box-shadow: 0 0 0 3px rgba(31, 138, 91, 0.18); }
.fp-dot-off { background: #c47b16; }

.fp-tiles {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}
@media (max-width: 1100px) { .fp-tiles { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 640px) { .fp-tiles { grid-template-columns: 1fr 1fr; } }
.fp-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  padding: 0.8rem 1rem;
  background: #fff;
  border: 1px solid var(--fp-line);
  border-top: 4px solid var(--fp-line);
  border-radius: 8px;
  text-align: left;
}
button.fp-tile { cursor: pointer; }
button.fp-tile:hover { border-color: #b9c9c1; }
.fp-tile.active { box-shadow: 0 0 0 2px rgba(15, 106, 124, 0.25); }
.fp-tile-label { font-size: 0.8rem; font-weight: 700; }
.fp-tile-value { font-size: 1.7rem; font-weight: 800; line-height: 1.15; }
.fp-tile-help { font-size: 0.74rem; color: var(--fp-muted); }
.fp-tile-danger { border-top-color: var(--fp-bad); }
.fp-tile-danger .fp-tile-label { color: var(--fp-bad); }
.fp-tile-info { border-top-color: var(--fp-info); }
.fp-tile-info .fp-tile-label { color: var(--fp-info); }
.fp-tile-warn { border-top-color: #d08c1c; }
.fp-tile-warn .fp-tile-label { color: var(--fp-warn); }
.fp-tile-money { border-top-color: var(--fp-ink); }
.fp-tile-ok { border-top-color: #1f8a5b; }
.fp-tile-ok .fp-tile-label { color: var(--fp-ok); }

.fp-card { border-radius: 8px; overflow: hidden; }
.fp-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.6rem 1rem 0;
  border-bottom: 1px solid var(--fp-line);
  background: #fafcfb;
}
.fp-tabs { border-bottom: 0; }
.fp-toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
}
.fp-filter-chip { cursor: pointer; font-size: 0.78rem; padding: 0.35rem 0.55rem; border: 1px solid var(--fp-line); }
.fp-search { position: relative; width: 280px; }
.fp-search .fa { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--fp-muted); font-size: 0.8rem; }
.fp-search input { padding-left: 28px; }

.fp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 2.5rem 1rem;
  color: var(--fp-muted);
}
.fp-empty .fa { font-size: 1.8rem; color: #9fbfb0; }

.fp-list { display: flex; flex-direction: column; }
.fp-row {
  border-bottom: 1px solid var(--fp-line);
  border-left: 4px solid transparent;
}
.fp-row-bad { border-left-color: var(--fp-bad); }
.fp-row-warn { border-left-color: #d08c1c; }
.fp-row-info { border-left-color: var(--fp-info); }
.fp-row-ok { border-left-color: #1f8a5b; }
.fp-row.open { background: #fbfdfc; }
.fp-row-main {
  display: grid;
  grid-template-columns: 110px minmax(150px, 1.1fr) 120px minmax(220px, 2fr) minmax(160px, 1fr) 24px;
  gap: 0.9rem;
  align-items: start;
  padding: 0.85rem 1rem 0.4rem;
  cursor: pointer;
}
@media (max-width: 992px) {
  .fp-row-main { grid-template-columns: 1fr 1fr; }
  .fp-col-toggle { display: none; }
}
.fp-order { font-weight: 800; font-size: 0.95rem; }
.fp-patient { font-weight: 700; }
.fp-amount { font-weight: 800; font-size: 1.05rem; }
.fp-muted { color: var(--fp-muted); font-size: 0.78rem; }
.fp-warn-text { color: var(--fp-warn); }
.fp-reason { font-weight: 700; }
.fp-advice {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: #3d4f49;
}
.fp-advice .fa { color: #c47b16; }
.fp-chips { display: flex; flex-wrap: wrap; gap: 0.3rem; margin-top: 0.35rem; }
.fp-chip {
  display: inline-block;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #eef2f0;
  font-size: 0.72rem;
  color: #3d4f49;
}
.fp-chip-code { font-family: SFMono-Regular, Menlo, monospace; background: #e9eef5; color: #334155; }
.fp-chip-warn { background: var(--fp-warn-bg); color: var(--fp-warn); }
.fp-risk-elevated { background: var(--fp-warn-bg); color: var(--fp-warn); }
.fp-risk-highest { background: var(--fp-bad-bg); color: var(--fp-bad); }
.fp-state {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
}
.fp-state-bad { background: var(--fp-bad-bg); color: var(--fp-bad); }
.fp-state-warn { background: var(--fp-warn-bg); color: var(--fp-warn); }
.fp-state-info { background: var(--fp-info-bg); color: var(--fp-info); }
.fp-state-ok { background: var(--fp-ok-bg); color: var(--fp-ok); }
.fp-state-muted { background: #eef2f0; color: var(--fp-muted); }
.fp-col-toggle { color: var(--fp-muted); padding-top: 0.2rem; }

.fp-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  padding: 0 1rem 0.8rem calc(110px + 1.9rem);
}
@media (max-width: 992px) { .fp-actions { padding-left: 1rem; } }
.fp-actions .fa { margin-right: 0.25rem; }
.fp-actions-spacer { flex: 1; }

.fp-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  padding: 0 1rem 1rem;
}
@media (max-width: 992px) { .fp-details { grid-template-columns: 1fr; } }
.fp-panel {
  background: #fff;
  border: 1px solid var(--fp-line);
  border-radius: 8px;
  padding: 0.8rem 1rem;
}
.fp-panel-wide { grid-column: 1 / -1; }
.fp-panel h3 {
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 0.5rem;
}
.fp-panel h3 small { text-transform: none; letter-spacing: 0; font-weight: 600; color: var(--fp-muted); margin-left: 0.3rem; }
.fp-panel-lead { font-weight: 700; margin: 0 0 0.2rem; }
.fp-dl {
  display: grid;
  grid-template-columns: 170px 1fr;
  gap: 0.25rem 0.75rem;
  margin: 0.5rem 0 0;
  font-size: 0.82rem;
}
.fp-dl dt { color: var(--fp-muted); font-weight: 600; }
.fp-dl dd { margin: 0; word-break: break-word; }
.fp-hold {
  padding: 0.5rem 0.7rem;
  border-radius: 6px;
  font-size: 0.86rem;
}
.fp-hold .fa { margin-right: 0.35rem; }
.fp-hold-ok { background: var(--fp-ok-bg); color: var(--fp-ok); }
.fp-hold-gone { background: var(--fp-bad-bg); color: var(--fp-bad); }
.fp-hold-unknown { background: #f3f5f4; color: var(--fp-muted); }

.fp-timeline {
  list-style: none;
  margin: 0;
  padding: 0 0 0 1rem;
  border-left: 2px solid var(--fp-line);
}
.fp-timeline li {
  position: relative;
  padding: 0 0 0.7rem 0.6rem;
}
.fp-timeline li::before {
  content: '';
  position: absolute;
  left: -1.42rem;
  top: 0.3rem;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--fp-bad);
  border: 2px solid #fff;
}
.fp-timeline li.ok::before { background: #1f8a5b; }
.fp-tl-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; font-size: 0.86rem; }
.fp-tl-reason { font-size: 0.82rem; margin-top: 0.15rem; }
</style>
